import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface AboutHeroProps {
  visible: boolean;
}

export default function AboutHero() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const scrollToBook = () => {
    const el = document.getElementById('book');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToStory = () => {
    const el = document.getElementById('brand-story');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/8339b38bc16b6e12931658c0ab74b992.jpg"
          alt="Book My Studio Space team"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/95 via-stone-950/75 to-stone-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-transparent to-stone-950/30" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-28 pb-24">
        <div className="max-w-3xl">
          <div
            className={`inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-bold uppercase tracking-widest px-5 py-2.5 rounded-full mb-8 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <i className="ri-building-4-line text-amber-400" />
            Our Mission &amp; Story
          </div>

          <h1
            className={`font-serif text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-[1.04] mb-7 transition-all duration-700 delay-100 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            About Book My{' '}
            <em className="text-amber-400 not-italic">Studio Space</em>
          </h1>

          <p
            className={`text-stone-300 text-xl md:text-2xl leading-relaxed mb-10 max-w-2xl transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Built for creators, by people who understand the creative process. We exist to give every professional access to the professional environment they deserve.
          </p>

          <div
            className={`flex flex-wrap items-center gap-4 mb-10 transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <button
              onClick={scrollToBook}
              className="group bg-amber-500 hover:bg-amber-600 text-white font-bold px-10 py-5 rounded-xl text-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-2.5"
            >
              Start Your First Booking
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={scrollToStory}
              className="flex items-center gap-2.5 text-white/90 hover:text-white border border-white/30 hover:border-white/70 font-semibold px-10 py-5 rounded-xl text-lg transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              Read Our Story
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-md border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 grid grid-cols-4 gap-4">
          {[
            { value: '50+', label: 'Studio Spaces' },
            { value: '6', label: 'Major Cities' },
            { value: '10k+', label: 'Creators Served' },
            { value: '4.9★', label: 'Avg. Rating' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-white font-bold text-xl md:text-2xl font-serif leading-none">{stat.value}</div>
              <div className="text-white/45 text-[11px] mt-1 uppercase tracking-wide">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
